import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { CheckCircle2, Target, Compass, Users, Factory, ShieldCheck, Award } from "lucide-react";
import { Reveal } from "../components/motion-primitives";

const IMG_DIRECTOR = "/director/director.png";
const IMG_ENG = "https://images.unsplash.com/photo-1709803857195-dbd9eb6dc4bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxmYWN0b3J5JTIwd29ya2VyJTIwdGFibGV0fGVufDB8fHx8MTc4Mzg0NTI1NHww&ixlib=rb-4.1.0&q=85";

const FACILITY_IMAGES = [
  {
    src: "/facility/BUILDWIT_20260801_134935_0000.jpg.jpeg",
    alt: "Buildwit facility view 1",
    label: "Functional production setup",
  },
  {
    src: "/facility/BUILDWIT_20260801_135024_0000.jpg.jpeg",
    alt: "Buildwit facility view 2",
    label: "Organized Machines",
  },
  {
    src: "/facility/BUILDWIT_20260801_135142_0000.jpg.jpeg",
    alt: "Buildwit facility view 3",
    label: "Assembly area",
  },
  {
    src: "/facility/BUILDWIT_20260801_135233_0000.jpg.jpeg",
    alt: "Buildwit facility view 4",
    label: "Smart store area",
  },
  {
    src: "/facility/BUILDWIT_20260801_135322_0000.jpg.jpeg",
    alt: "Buildwit facility view 5",
    label: "Heavy duty Machines",
  },
];

const VALUES = [
  { icon: ShieldCheck, t: "Integrity", d: "Documented, auditable engineering." },
  { icon: Award, t: "Excellence", d: "Sub-micron precision as a baseline." },
  { icon: Users, t: "Partnership", d: "One engineer accountable for every install." },
  { icon: Factory, t: "Make in India", d: "Design, cast, machine, assemble — all in-house." },
];

export default function About() {
  return (
    <>
      <section className="relative bg-brand-navy text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={IMG_ENG} alt="" className="w-full h-full object-cover"/>
        </div>
        <div className="absolute inset-0 bg-brand-navy/70"/>
        <div className="relative section-x mx-auto max-w-[1440px] py-24 md:py-32">
          <div className="overline text-accent">About Buildwit</div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16,1,0.3,1] }}
            className="mt-4 font-display text-5xl md:text-7xl font-black tracking-tight leading-[1.02] max-w-4xl text-balance"
          >
            Making machines that governments rely on.
          </motion.h1>
          <p className="mt-6 max-w-3xl text-slate-300 text-lg leading-relaxed">
            Buildwit Technology is a Faridabad-based industrial automation manufacturer, delivering integrated machinery to government PSUs, defense, railways, pharma and heavy industries.
          </p>
        </div>
      </section>

      {/* VISION MISSION */}
      <section className="section-x mx-auto max-w-[1440px] py-24 grid md:grid-cols-2 gap-6">
        {[{ icon: Compass, t: "Vision", d: "To become India's most trusted engineering partner for automated industrial machinery — a benchmark for precision, uptime and lifetime support." },
          { icon: Target, t: "Mission", d: "To design, build and commission machinery that our clients depend on for decades — engineered end-to-end in India, backed by lifetime service." }
        ].map((b, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="relative h-full rounded-2xl border border-slate-200 bg-white p-10">
              <span className="corner-mark tl"/><span className="corner-mark tr"/><span className="corner-mark bl"/><span className="corner-mark br"/>
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-brand-navy text-white"><b.icon className="h-6 w-6"/></span>
              <h3 className="mt-6 font-display text-3xl font-extrabold text-brand-navy tracking-tight">{b.t}</h3>
              <p className="mt-3 text-slate-600 text-base leading-relaxed">{b.d}</p>
            </div>
          </Reveal>
        ))}
      </section>

      {/* VALUES */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="section-x mx-auto max-w-[1440px] py-24">
          <div className="overline text-accent">Core values</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-extrabold text-brand-navy tracking-tight leading-tight max-w-2xl">Four principles engineered into every machine.</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="rounded-xl border border-slate-200 bg-white p-7 h-full">
                  <v.icon className="h-6 w-6 text-accent"/>
                  <div className="mt-4 font-display text-lg font-bold text-brand-navy">{v.t}</div>
                  <div className="mt-1 text-sm text-slate-500">{v.d}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FACILITY */}
      <section className="section-x mx-auto max-w-[1440px] py-24 overflow-hidden">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-4">
            <div className="max-w-xl">
              <div className="overline text-accent">Our facility in motion</div>
              <h2 className="mt-3 font-display text-4xl md:text-5xl font-extrabold text-brand-navy tracking-tight leading-tight text-balance">
                A closer look at the floor where ideas become machines.
              </h2>
              <p className="mt-6 text-slate-600 text-lg leading-relaxed">
                Our facility in Faridabad is designed for precision, efficiency and safety. Every machine is cast, machined, assembled and tested under one roof — with a dedicated engineer accountable for every project.
              </p>
              <div className="mt-8 flex flex-wrap gap-3 text-sm text-slate-600">
                <span className="rounded-full border border-slate-200 bg-white px-4 py-2">Assembly</span>
                <span className="rounded-full border border-slate-200 bg-white px-4 py-2">Machining</span>
                <span className="rounded-full border border-slate-200 bg-white px-4 py-2">Inspection</span>
                <span className="rounded-full border border-slate-200 bg-white px-4 py-2">Commissioning</span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.08} className="lg:col-span-8">
            <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-4 md:p-5 shadow-[0_24px_80px_-48px_rgba(10,37,64,0.45)]">
              <span className="corner-mark tl" />
              <span className="corner-mark tr" />
              <span className="corner-mark bl" />
              <span className="corner-mark br" />
              <div className="absolute inset-0 hero-grid opacity-30" />

              <div className="relative space-y-4">
                <Marquee gradient gradientColor="#ffffff" gradientWidth={64} speed={26} pauseOnHover>
                  {FACILITY_IMAGES.map((image) => (
                    <div key={image.label} className="mx-3 w-[260px] sm:w-[300px]">
                      <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
                        <div className="aspect-[4/3] overflow-hidden">
                          <img
                            src={image.src}
                            alt={image.alt}
                            className="h-full w-full object-cover transition duration-700 ease-out group-hover:scale-105"
                          />
                        </div>
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-navy/80 via-brand-navy/20 to-transparent px-4 pb-4 pt-12">
                          <div className="text-xs uppercase tracking-[0.2em] text-white/70">Facility view</div>
                          <div className="mt-1 font-display text-lg font-bold text-white">{image.label}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </Marquee>

                <Marquee gradient gradientColor="#ffffff" gradientWidth={64} speed={20} direction="right" pauseOnHover>
                  {FACILITY_IMAGES.slice().reverse().map((image) => (
                    <div key={`${image.label}-reverse`} className="mx-3 w-[220px] sm:w-[250px]">
                      <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
                        <div className="aspect-[5/4] overflow-hidden">
                          <img
                            src={image.src}
                            alt={image.alt}
                            className="h-full w-full object-cover transition duration-700 ease-out hover:scale-105"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </Marquee>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* DIRECTOR MSG */}
      <section className="section-x mx-auto max-w-[1440px] py-24 grid md:grid-cols-12 gap-10 items-center">
        <Reveal className="md:col-span-5">
          <div className="relative rounded-2xl overflow-hidden aspect-[4/5] border border-slate-200">
            <img src={IMG_DIRECTOR} alt="Manufacturing facility" className="w-full h-full object-cover"/>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-7">
          <div className="overline text-accent">Director&apos;s message</div>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-extrabold text-brand-navy tracking-tight leading-tight">&ldquo;We don&apos;t ship products. We ship promises that must run for 20 years.&rdquo;</h2>
          <p className="mt-6 text-slate-600 text-lg leading-relaxed">
            Every machine we deliver is expected to run through three shifts, in demanding conditions, for two decades. That standard shapes every decision we make — from the grade of steel we cast to the engineer we assign at commissioning.
          </p>
          <div className="mt-8 pt-6 border-t border-slate-200">
            <div className="font-display font-bold text-brand-navy">Tripurari Jha</div>
            <div className="text-sm text-slate-500">Managing Director, Buildwit Technology Pvt. Ltd.</div>
          </div>
        </Reveal>
      </section>

      {/* QUALITY */}
      <section className="section-x mx-auto max-w-[1440px] py-24">
        <div className="grid md:grid-cols-2 gap-14 items-start">
          <div>
            <div className="overline text-accent">Quality standards</div>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-extrabold text-brand-navy tracking-tight leading-tight">Every machine passes 217 checkpoints before dispatch.</h2>
          </div>
          <ul className="space-y-4">
            {[
              "ISO 9001:2015 Quality Management System",
              "CE marking for European exports",
              "BIS registration for domestic supply",
              "In-house CMM & vibration analysis",
              "Documented URS → FAT → SAT for every project",
              "10-year traceability on critical components",
            ].map((it, i) => (
              <li key={i} className="flex items-start gap-3 text-slate-700 text-base">
                <CheckCircle2 className="h-5 w-5 text-accent mt-0.5 shrink-0"/> {it}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}
